import React, { useState, useEffect } from 'react';
import {
  getPurchaseOrders,
  PurchaseOrder,
  PurchaseOrderStatus,
} from '../../services/purchaseOrderService';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import PurchaseOrderDetails from './PurchaseOrderDetails';
import CreatePOForm from './CreatePOForm';

const PurchaseOrderList: React.FC = () => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [filterStatus, setFilterStatus] = useState<PurchaseOrderStatus | 'ALL'>('ALL');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editPO, setEditPO] = useState<PurchaseOrder | null>(null);

  const loadPurchaseOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filterStatus !== 'ALL' ? { status: filterStatus as PurchaseOrderStatus } : {};
      const data = await getPurchaseOrders(params);
      setPurchaseOrders(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPurchaseOrders();
  }, [filterStatus]);

  const getStatusBadge = (status: PurchaseOrderStatus) => {
    const styles = {
      [PurchaseOrderStatus.DRAFT]: 'bg-gray-100 text-gray-800',
      [PurchaseOrderStatus.SENT]: 'bg-blue-100 text-blue-800',
      [PurchaseOrderStatus.PARTIAL]: 'bg-yellow-100 text-yellow-800',
      [PurchaseOrderStatus.COMPLETED]: 'bg-green-100 text-green-800',
      [PurchaseOrderStatus.CANCELLED]: 'bg-red-100 text-red-800',
    };

    const labels = {
      [PurchaseOrderStatus.DRAFT]: 'Draft',
      [PurchaseOrderStatus.SENT]: 'Sent',
      [PurchaseOrderStatus.PARTIAL]: 'Partial',
      [PurchaseOrderStatus.COMPLETED]: 'Completed',
      [PurchaseOrderStatus.CANCELLED]: 'Cancelled',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const canReceive = (po: PurchaseOrder): boolean => {
    return po.status === PurchaseOrderStatus.SENT || po.status === PurchaseOrderStatus.PARTIAL;
  };

  const getDaysSinceUpdate = (po: PurchaseOrder): number => {
    const updatedAt = po.created_at; // Since updated_at not in interface, use created_at
    if (!updatedAt) return 0;
    
    try {
      const date = new Date(updatedAt);
      if (isNaN(date.getTime())) return 0;
      
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      return 0;
    }
  };

  const getStaleWarning = (po: PurchaseOrder) => {
    if (po.status !== PurchaseOrderStatus.PARTIAL && po.status !== 'partial') {
      return null;
    }
    
    const daysSinceUpdate = getDaysSinceUpdate(po);
    
    if (daysSinceUpdate >= 30) {
      return (
        <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {daysSinceUpdate} hari
        </span>
      );
    } else if (daysSinceUpdate >= 7) {
      return (
        <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
          ⚠️ {daysSinceUpdate} hari
        </span>
      );
    }
    
    return null;
  };

  const getStatusLabel = (status: PurchaseOrderStatus): string => {
    const labels = {
      [PurchaseOrderStatus.DRAFT]: 'Draft',
      [PurchaseOrderStatus.SENT]: 'Terkirim',
      [PurchaseOrderStatus.PARTIAL]: 'Sebagian',
      [PurchaseOrderStatus.COMPLETED]: 'Selesai',
      [PurchaseOrderStatus.CANCELLED]: 'Dibatalkan',
    };
    return labels[status];
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Invalid date format:', dateString, error);
      return '-';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <ErrorMessage message={error} onRetry={loadPurchaseOrders} />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Details Modal */}
      {selectedPO && (
        <PurchaseOrderDetails
          poId={selectedPO.id}
          onClose={() => setSelectedPO(null)}
          onUpdate={loadPurchaseOrders}
        />
      )}

      {/* Create/Edit Form Modal */}
      {(showCreateForm || editPO) && (
        <CreatePOForm
          onClose={() => {
            setShowCreateForm(false);
            setEditPO(null);
          }}
          onSuccess={loadPurchaseOrders}
          editData={editPO}
        />
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Purchase Order</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Buat PO Baru
          </button>
        </div>

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterStatus('ALL')}
            className={`px-4 py-2 rounded ${
              filterStatus === 'ALL'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Semua
          </button>
          {Object.values(PurchaseOrderStatus).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded ${
                filterStatus === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {getStatusLabel(status)}
            </button>
          ))}
        </div>
      </div>

      {/* PO List */}
      {purchaseOrders.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-lg">Tidak ada purchase order</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nomor PO
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tgl Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tgl Estimasi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {purchaseOrders.map((po) => {
                  const totalItems = po.items.reduce((sum, item) => sum + item.qty_ordered, 0);
                  const totalReceived = po.items.reduce((sum, item) => sum + item.qty_received, 0);
                  
                  return (
                    <tr key={po.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{po.po_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(po.order_date)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {po.expected_date ? formatDate(po.expected_date) : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(po.status)}
                          {getStaleWarning(po)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {po.items.length} item ({totalReceived}/{totalItems} diterima)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(po.total_amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => setSelectedPO(po)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                        >
                          Lihat Detail
                        </button>
                        {canReceive(po) && totalReceived < totalItems && (
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                            Bisa Terima
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderList;
